import camelCase from 'lodash/camelCase';
import { customEventFactory, NEW_RELIC_FORM_NAME } from './customEventFactory';
import { newRelicService } from './newRelicService';

export const addGlobalFormSubmitEventListeners = () => {
  document.addEventListener('click', handleButtonClickEvent);
  document.addEventListener(
    'submit',
    handleFormSubmitEvent,
    // Important to have capture=true to catch the event before it,
    // with high chance, will be prevented by React or other
    // code (preventDefault, stopPropagation, etc.)
    true,
  );
};

export const destroyGlobalFormSubmitEventListeners = () => {
  document.removeEventListener('click', handleButtonClickEvent);
  document.removeEventListener('submit', handleFormSubmitEvent, true);
};

const FORM_CLASS_BEM_PATTERN = /^(.+?)__/;
const BUTTON_FORM_CLASS_PATTERN = /^(.+?)__form/;
const DATA_FORM_ID_ATTR = 'data-form-id';
const DEFAULT_BUTTON_TEXT = 'submit';

/**
 * Tracks form-submit attempts triggered by non-submit buttons (e.g. React forms that
 * handle submission in onClick instead of a native submit).
 *
 * Only considers buttons, skip other elements.
 *
 * Resolves form name via resolveFormNameFromButton. If form name cannot
 * be extracted (resolveFormNameFromButton returns null),
 * no event is tracked.
 */
function handleButtonClickEvent(event) {
  const button = event.target;

  if (button.tagName !== 'BUTTON') {
    return;
  }

  const formName = resolveFormNameFromButton(button);

  if (formName) {
    trackFormSubmit(formName, getButtonText(button));
  }
}

/**
 * Tracks native form submit attempts (submit button, Enter in a field, requestSubmit, etc.).
 * Registered in capture phase so the event is seen before React preventDefault/stopPropagation.
 *
 * Resolves form name via getFormNameFromForm
 */
function handleFormSubmitEvent(event) {
  const form = event.target;

  if (!(form instanceof HTMLFormElement)) {
    return;
  }

  // If the form is submitted by a button with a data-form-id attribute,
  // we don't want to track the form submit event because it's
  // already tracked by the button click event.
  if (form.submitter?.getAttribute(DATA_FORM_ID_ATTR)) {
    console.debug('Form submitted by a button with a data-form-id attribute, skipping form submit event tracking.', {
      form,
    });
    return;
  }

  trackFormSubmit(getFormNameFromForm(form), getButtonText(form.submitter));
}

/**
 * Returns form name from form tag:
 * 1. Form id, if present
 * 2. Else first class matching "{form_name}__*" (BEM block name before __)
 * 3. else "unknown"
 *
 * @param {HTMLFormElement} form
 * @returns {string}
 */
function getFormNameFromForm(form) {
  if (form.id) {
    return form.id;
  }

  for (const className of form.classList) {
    const match = className.match(FORM_CLASS_BEM_PATTERN);

    if (match) {
      return match[1];
    }
  }

  return NEW_RELIC_FORM_NAME.UNKNOWN;
}

/**
 * Returns form name from button tag:
 * 1. Return data-form-id attribute value if present
 * 2. Otherwise, if class matches "{name}__form*" → use {name}
 * 3. Otherwise, return null.
 *
 * @param {HTMLButtonElement} button
 * @returns {string | null}
 */
function resolveFormNameFromButton(button) {
  const dataFormId = button.getAttribute(DATA_FORM_ID_ATTR);

  if (dataFormId) {
    return dataFormId;
  }

  for (const className of button.classList) {
    const match = className.match(BUTTON_FORM_CLASS_PATTERN);
    if (match) {
      return match[1];
    }
  }

  return null;
}

/**
 * @param {HTMLElement | null | undefined} element
 * @returns {string}
 */
function getButtonText(element) {
  if (!element) {
    return DEFAULT_BUTTON_TEXT;
  }

  if (element instanceof HTMLButtonElement) {
    const text = element.textContent?.trim();
    return text || element.value?.trim() || DEFAULT_BUTTON_TEXT;
  }

  return DEFAULT_BUTTON_TEXT;
}

/**
 * @param {string} form
 * @param {string} buttonText
 */
function trackFormSubmit(form, buttonText) {
  const newRelic = newRelicService.getInstance();
  if (!newRelic) return;
  const formName = camelCase(form);
  customEventFactory.formSubmit({
    newRelic,
    form: formName,
    buttonText,
  });
  console.info('New Relic form submit event tracked.', { form: formName, buttonText });
}
